import React from "react";
import { Button, Label, Text } from "@jsq/ds/core";
import { Form, SubmitButton } from "@jsq/ds/forms";
import { WorkflowMode } from "../../common/enums";
import { formTextMap } from "./constants";
import { backButton, footer, paymentMethodContainer } from "./styles_vanilla.css";
import { type PaymentPrefLayoutFormProps } from "./types";

/**
 * PaymentPrefLayoutForm component.
 *
 * This component renders a form for creating new payment instructions. It includes
 * fields for payment method, children components for additional form fields, and
 * buttons for submitting the form or going back.
 *
 * @param {Object} props - The component props.
 * @param {PaymentMethod} props.paymentMethod - The current payment method.
 * @param {Function} props.onSubmit - The function to call when the form is submitted.
 * @param {Function} props.onBackClick - The function to call when the back button is clicked.
 * @param {Object} props.initialValues - The initial values for the form fields.
 * @param {Object} props.validationSchema - The validation schema for the form fields.
 * @param {string} [props.onBackButtonName="Back"] - The text to display on the back button.
 * @param {React.ReactNode} props.children - The children components to render inside the form.
 */
export const PaymentPrefLayoutForm: React.FC<PaymentPrefLayoutFormProps> = ({
  paymentMethod,
  onSubmit,
  onBackClick,
  initialValues,
  validationSchema,
  onBackButtonName = "Back",
  workflowMode = WorkflowMode.Create,
  children,
}) => {
  const formText = formTextMap.get(paymentMethod);

  return (
    <Form
      // @ts-expect-error: this form with initial values of type T should expect onSubmit of type (values: T) => void
      onSubmit={onSubmit}
      submitOnlyChangedValues={false}
      // @ts-expect-error: initial values will be handled by the correct payment pref type downstream
      initialValues={initialValues}
      validationSchema={validationSchema}
    >
      <Text variant="h3">
        {workflowMode === WorkflowMode.Create
          ? "Create new payment instructions"
          : formText?.editWorkflowTitle}
      </Text>
      <div className={paymentMethodContainer}>
        <Label>Payment method</Label>
        <Text variant="regular-md">{formText?.paymentMethodText}</Text>
      </div>
      {children}
      <div className={footer}>
        <Button className={backButton} variant="neutral" onClick={onBackClick}>
          {onBackButtonName}
        </Button>
        <SubmitButton>Continue</SubmitButton>
      </div>
    </Form>
  );
};
